import React, { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import RouteLoadingFallback from '../infrastructure/RouteLoadingFallback.js';
import { canAccessSkillWithState } from '../infrastructure/paywallConfig.js';
import { setGuestModeActive } from '../infrastructure/guestMode.js';
import { navigateToLearnMoreRenew } from './learnMoreNavigation.js';
import { usePaywall } from './PaywallProvider.js';

function parseSkillIdFromLocation(location, defaultSkillId) {
  const params = new URLSearchParams(location.search);
  const fromQuery = params.get('skillId');
  if (fromQuery != null && fromQuery !== '') {
    return Number(fromQuery);
  }
  if (defaultSkillId != null && defaultSkillId !== '') {
    return Number(defaultSkillId);
  }
  return null;
}

/**
 * Gate skill practice routes: guests get always-available skills;
 * signed-in users also get login-required free skills.
 */
export default function SkillAccessGate({
  isAuthenticated,
  authChecked,
  userId,
  defaultSkillId = null,
  setIsAuthenticated,
  setUserId,
  children,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { access, accessLoading, openSignInModal } = usePaywall();

  const skillId = useMemo(
    () => parseSkillIdFromLocation(location, defaultSkillId),
    [location, defaultSkillId]
  );

  const canAccess = useMemo(() => {
    if (!Number.isFinite(skillId)) {
      return isAuthenticated;
    }
    return canAccessSkillWithState(access, skillId);
  }, [access, isAuthenticated, skillId]);

  const isGuest = !isAuthenticated && canAccess;

  useEffect(() => {
    setGuestModeActive(isGuest);
    return () => setGuestModeActive(false);
  }, [isGuest]);

  if (!authChecked || accessLoading) {
    return <RouteLoadingFallback />;
  }

  if (!canAccess) {
    return (
      <SkillAccessBlocked
        isAuthenticated={isAuthenticated}
        access={access}
        skillId={skillId}
        location={location}
        openSignInModal={openSignInModal}
        navigate={navigate}
      />
    );
  }

  const effectiveUserId = isGuest ? '' : userId;

  return (
    <>
      {React.Children.map(children, (child) => (
        React.isValidElement(child)
          ? React.cloneElement(child, { userId: effectiveUserId })
          : child
      ))}
    </>
  );
}

function SkillAccessBlocked({
  isAuthenticated,
  access,
  skillId,
  location,
  openSignInModal,
  navigate,
}) {
  useEffect(() => {
    if (isAuthenticated && access?.tier === 'expired') {
      navigateToLearnMoreRenew(navigate, location);
      return;
    }
    openSignInModal({
      reason: 'skill-locked',
      ...(Number.isFinite(skillId) ? { skillId } : {}),
    });
  }, [access?.tier, isAuthenticated, location, navigate, openSignInModal, skillId]);
  return null;
}
